// client/src/coms/stickers/localStickerRelay.js
// Sending a sticker from the composer. Server-hosted stickers already have a token (<sticker:ID>). A local
// sticker exists only on this device, so its image is uploaded once to the sender's private relay pack
// (POST /stickers/local/send) and the returned token is sent like any other sticker; recipients render the
// message from the server and get nothing else. The relay id is remembered per account on the sticker, so
// the same sticker is uploaded only once.
import { uploadMultipart } from '../imagePrep';
import { stickerResolver } from '../customMedia';
import { getLocalSticker, updateLocalSticker } from './localStickerStore';

let mePromise = null;

function currentUserId() {
  if (!mePromise) {
    mePromise = fetch('/api/me', { credentials: 'include', cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((me) => String((me && (me.id != null ? me.id : me.user_id)) || ''))
      .catch(() => '');
    mePromise.then((id) => {
      if (!id) mePromise = null; // try again next time
    });
  }
  return mePromise;
}

const EXT = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/gif': 'gif', 'image/webp': 'webp' };

// Returns the token to send for a sticker from the picker or suggestions, or throws with a readable message.
export async function tokenForSticker(sticker, { fresh = false } = {}) {
  if (!sticker) throw new Error('No sticker');
  if (!sticker.local) return sticker.token;
  const record = await getLocalSticker(sticker.localId);
  if (!record || !record.blob) throw new Error('That local sticker is no longer on this device');
  const uid = await currentUserId();
  const known = record.relay && uid ? record.relay[uid] : null;
  if (!fresh && known && known.sticker_id && known.size === record.blob.size) return `<sticker:${known.sticker_id}>`;
  const file = new File([record.blob], `${(record.name || 'sticker').replace(/[^A-Za-z0-9_-]+/g, '_')}.${EXT[record.mime] || 'png'}`, { type: record.mime || record.blob.type });
  const result = await uploadMultipart('/stickers/local/send', { name: record.name || 'sticker' }, file);
  const owner = String(result.owner_user_id || uid || '');
  if (owner) {
    await updateLocalSticker(record.id, { relay: { ...(record.relay || {}), [owner]: { sticker_id: result.sticker_id, size: record.blob.size, at: Date.now() } } }, { silent: true }).catch(() => {});
  }
  // The message renders straight away from the uploaded image.
  stickerResolver.prime([{ id: result.sticker_id, name: record.name, url: result.url, animated: !!result.animated }]);
  return result.token;
}
